// src/oura/components/search/PctChange.tsx
//
// A change on the Search performance screen: "▲ 15.6% on the 28 days before"
// for the Search Console and Analytics totals, and a search's position
// movement in the searches table. One arrow, one screen-reader word and one
// pair of colours for both.
//
// These do not go through trendBreak.ts, and need not: that module guards a
// change measured across a re-authored question set. Both of these compare
// two consecutive windows Google itself counted the same way, with no
// question set involved. The one case that is not comparable is the one the
// backend already marks, a first window with nothing before it (`null`), and
// the caller says so in words instead of rendering this.

import type { ReactNode } from "react";
import { cn } from "../../lib/cn";

/** The mark for a change that went up (green ▲) or down (rose ▼), read out as "Up" or "Down". */
export function Change({ up, children }: { up: boolean; children: ReactNode }) {
    return (
        <span className={cn("font-medium tabular-nums", up ? "text-emerald-700" : "text-rose-700")}>
            <span aria-hidden>{up ? "▲" : "▼"} </span>
            <span className="sr-only">{up ? "Up " : "Down "}</span>
            {children}
        </span>
    );
}

export function PctChange({ percent, days }: { percent: number; /** The window compared against. */ days: number }) {
    const tail = ` on the ${days} days before`;
    if (percent === 0) return <span>No change{tail}</span>;
    return (
        <span>
            {/* One place at most, and none when it is whole: "19%", "15.6%". */}
            <Change up={percent > 0}>{Number(Math.abs(percent).toFixed(1))}%</Change>
            {tail}
        </span>
    );
}
